import { useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { QueryClientContext, type QueryClient } from '@tanstack/react-query'
import * as Dialog from '@radix-ui/react-dialog'
import {
  X,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  Camera,
  Calendar,
  Clock,
  MapPin,
  ImageOff,
  Loader2,
  Sparkles,
  Star,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { eventsApi } from '@/api/endpoints'  // #329
import { queryKeys } from '@/config/queryConfig'  // #329
import { cn } from '@/lib/utils'
import { formatDateTime, formatCardDateTime } from '@/lib/utils'
import { getEventDownloads, downloadFile } from '@/lib/eventDownload'
import { useFullSizePhoto, type FullSizeState } from '@/hooks/useFullSizePhoto'  // #398
import { useToggleFavorite } from '@/hooks/useToggleFavorite'
import { useMarkEventRead } from '@/hooks/useMarkEventRead'
import { useEventTitle } from '@/hooks/useEventTitle'  // #294
import { isSuspiciousEvent } from '@/lib/suspiciousEvents'  // #406
import { SuspiciousDetails } from './SuspiciousEventRow'  // #406
import { isCriticalEvent } from '@/lib/criticalEvents'  // #294
import { CriticalReasonChips } from './CriticalEventRow'  // #294
import { EventLocationMap } from './EventLocationMap'
import { PoseLayer } from './PoseOverlay'  // 409
import { PoseReviewPanel } from './PoseReviewMarks'  // 409
import { LocationDetails } from '@/components/maps/LocationDetails'
import type { Event, VisualDetection } from '@/types'
// Detection icon imports moved to shared DetectionChip component

// DetectionChip — imported from shared UI component (aliased as ModalChip for local usage)
import { DetectionChip as ModalChip } from '@/components/ui/detection-chip'

// ─── Media panel with pinch/drag zoom ────────────────────────────────────────

const MIN_SCALE = 1
const MAX_SCALE = 5

// Matches the Radix Dialog open animation (data-[state=open]:duration-350) with a small
// buffer so the <video> element mounts only after the modal is fully settled. This is the
// foundation of the two-phase video reveal — see ModalMedia.
const MODAL_OPEN_ANIMATION_MS = 380

// Below this, a load is imperceptible and a spinner would only flash. Above it, the user
// deserves to know something is happening.
const SPINNER_DELAY_MS = 150

// #329 — the playable (H.264) copy of a new video lands seconds after the event: ask the server for that one
// event until the link is there: the first 10 requests 3 s apart, then 5 s apart, 40 in all (30 s + 150 s =
// 3 min; #331 — a busy handler can take longer than the first minute). "Retry" starts another cycle.
const CLIP_POLL_MAX = 40
const delayFor = (call: number) => (call <= 10 ? 3000 : 5000)

// A new video older than this is not "being prepared" any more: it just has no playable copy, and the popup
// shows the quiet still frame as before #329.
const CLIP_FRESH_MS = 10 * 60 * 1000

// The app always has a QueryClientProvider; reading the context directly keeps the modal renderable on its own
// in tests that mount it bare (useQueryClient throws without a provider).
function useOptionalQueryClient(): QueryClient | null {
  return useContext(QueryClientContext) ?? null
}

function clamp(val: number, min: number, max: number) {
  return Math.min(Math.max(val, min), max)
}

function ModalMedia({ event, fullSizeUrl, onShownResolution }: {
  event: Event
  fullSizeUrl: string | null
  // #398 — tells the detail panel the pixel size of the full-size photo while it is on screen (null otherwise)
  onShownResolution?: (value: string | null) => void
}) {
  const { t } = useTranslation('events')

  const isVideo = event.media_details?.media_type === 'video'
  // #329 — the first playable link seen for this event (from its own row or from asking the server) is the one
  // that plays. Presigned links differ between gunicorn workers, so a list refresh can bring another link for the
  // same clip; a <video> keyed on the URL would restart. readyUrl resets only when the event changes: ModalMedia
  // is rendered with key={event.id}, so the remount does that reset.
  const [readyUrl, setReadyUrl] = useState<string | null>(null)
  const videoUrl = readyUrl ?? event.detection_video_url
  // #332 — the playable copy will never come (the handler's conversion or upload failed): the row says so (REST, or
  // the WebSocket push patched it while the popup is open), or the server said so to this popup's own request (the
  // push was missed). A link, when there is one, still plays: the handler never marks a stored clip failed.
  const [serverFailed, setServerFailed] = useState(false)
  const clipFailed = isVideo && !videoUrl && (event.video_status === 'failed' || serverFailed)
  const waiting = isVideo && !videoUrl && !clipFailed
  // only a fresh event (created_at, else occurred_at) is worth waiting for
  const [fresh] = useState(() => {
    const at = Date.parse(event.created_at ?? event.occurred_at ?? '')
    return Number.isFinite(at) && Date.now() - at < CLIP_FRESH_MS
  })
  const [stopped, setStopped] = useState(false)   // 404/401/403: nothing to wait for
  const statusRef = useRef<HTMLDivElement>(null)
  // #331 — the clip has a link but the browser could not load it (expired link, 403, damaged file)
  const [videoError, setVideoError] = useState(false)
  const [reloads, setReloads] = useState(0)   // bumped by every Retry answer with a link
  const [retrying, setRetrying] = useState(false)
  const retryingRef = useRef(false)
  const showStatus = clipFailed || (waiting && fresh && !stopped) || videoError
  const queryClient = useOptionalQueryClient()
  const [pollRound, setPollRound] = useState(0)   // bumped by Retry
  const [gaveUp, setGaveUp] = useState(false)
  const mountedRef = useRef(true)
  useEffect(() => () => { mountedRef.current = false }, [])
  const thumbnailUrl = event.thumbnail_url
  // #398 — once the camera's full-size photo is attached it is shown; the viewer can switch back to the thumbnail
  const [showThumb, setShowThumb] = useState(false)
  const imageUrl = fullSizeUrl && !showThumb ? fullSizeUrl : thumbnailUrl
  // #398 — the media row knows only the thumbnail's size; the full-size photo's own size is read from the
  // loaded <img> and handed up while that photo is the one on screen.
  const fullSizeOnScreen = Boolean(fullSizeUrl) && !showThumb
  useEffect(() => {
    if (!fullSizeOnScreen) onShownResolution?.(null)
  }, [fullSizeOnScreen, onShownResolution])

  const [imageStatus, setImageStatus] = useState<'loading' | 'loaded' | 'error'>(
    imageUrl ? 'loading' : 'error'
  )
  // #078: two-phase video reveal.
  // Phase 1 (modal-open animation): only the thumbnail <img> is rendered.
  // Phase 2 (after MODAL_OPEN_ANIMATION_MS): the <video> mounts at opacity 0 with autoplay;
  // when it fires `playing`, fade it in over the thumbnail. This eliminates the race between
  // the Radix open animation and the browser's media-pipeline init (metadata load, decoder
  // allocation, controls paint, first-frame paint) that produced the "nudge" we saw.
  const [modalSettled, setModalSettled] = useState(false)
  const [videoPlaying, setVideoPlaying] = useState(false)

  // #319 — the clip starts WITH sound (owner 2026-10-12: "videos volume full instead of mute when it starts
  // playing"). The click that opened the modal is the user gesture browsers want before sound, so in Chrome,
  // Edge and Firefox the sound starts at once. Where a browser refuses — Safari on iPhone, low-power mode,
  // Safari on a Mac the first time, or a page opened straight on an event (a shared link or a reload with the
  // modal open: no gesture at all) — the clip must not stay a black box: it falls back to muted and plays, and
  // the controls' speaker button turns the sound on.
  // A callback ref, stable across renders: it runs once per mounted <video> (the key on the URL remounts it).
  const startWithSound = useCallback((video: HTMLVideoElement | null) => {
    if (!video) return
    video.muted = false
    video.volume = 1
    const attempt = video.play()
    if (!attempt || typeof attempt.catch !== 'function') return   // jsdom and very old browsers: no promise
    attempt.catch((err: unknown) => {
      // 319 review — the modal closed, or moved on to the next event, while the browser was still answering
      // (AbortError for the removed element): never play a detached element, it would download and loop,
      // muted, with nothing left to stop it.
      if (!video.isConnected) return
      // Only a refusal for lack of a user gesture is helped by a muted retry (NotAllowedError in Chrome,
      // Firefox and Safari). A bad or expired source fails the same way muted; leave it.
      if ((err as { name?: string } | null)?.name !== 'NotAllowedError') return
      video.muted = true
      const retry = video.play()
      if (retry && typeof retry.catch === 'function') retry.catch(() => undefined)
    })
  }, [])
  // 409 — the pose overlay reads the same <video> element
  const videoElRef = useRef<HTMLVideoElement | null>(null)
  const videoRefCallback = useCallback((video: HTMLVideoElement | null) => {
    videoElRef.current = video
    startWithSound(video)
  }, [startWithSound])

  // ── Zoom / pan state ──
  const [scale, setScale] = useState(1)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const containerRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)

  // Derived
  const isZoomed = scale > 1.01

  // Reset all transient state when navigating to a different event.
  //
  // NB: do not try to detect a cached image here by probing `imgRef.current.complete`.
  // It does not work — `complete` is false in the same tick the <img> is created, even
  // when the bytes are sitting in the HTTP cache, because the load has not been *completed*
  // yet. (A unit test can be made to pass by stubbing `complete`, which proves only that
  // the stub works.) The image is instead made glitch-free by never fading it: see the
  // <img> below.
  useLayoutEffect(() => {
    setScale(1)
    setOffset({ x: 0, y: 0 })
    setModalSettled(false)
    setVideoPlaying(false)
    setImageStatus(imageUrl ? 'loading' : 'error')
  }, [event.id, imageUrl])

  // A cached thumbnail settles within a frame or two, and a spinner that appears and
  // vanishes in 30ms is itself a flicker. Only show it if the image genuinely makes us wait.
  const [showSpinner, setShowSpinner] = useState(false)
  useEffect(() => {
    if (imageStatus !== 'loading') {
      setShowSpinner(false)
      return
    }
    const id = window.setTimeout(() => setShowSpinner(true), SPINNER_DELAY_MS)
    return () => window.clearTimeout(id)
  }, [imageStatus])

  // Phase 1 → Phase 2 transition: wait until the modal's open animation has fully run
  // before mounting the <video> element. During this window only the thumbnail is on screen
  // (it's an <img>, so its size lands with the first paint — no layout shift).
  useEffect(() => {
    if (!isVideo) return
    const id = window.setTimeout(() => setModalSettled(true), MODAL_OPEN_ANIMATION_MS)
    return () => window.clearTimeout(id)
  }, [isVideo, event.id])

  // #329 — the first playable link seen is kept (see readyUrl above).
  useEffect(() => {
    if (!readyUrl && event.detection_video_url) setReadyUrl(event.detection_video_url)
  }, [readyUrl, event.detection_video_url])

  // #329 — wait for the playable copy of a brand-new video: poll this one event, never for images or for
  // videos that already have a link. The schedule is 3 s apart for the first 10 requests, then 5 s apart, 40 in
  // all (#331). The cleanup clears the timer (popup closed, event changed, unmount).
  useEffect(() => {
    if (!waiting || !fresh || stopped) return
    setGaveUp(false)
    let calls = 0
    let cancelled = false
    let timer: number | undefined
    const ask = async () => {
      calls += 1
      let stop = false
      try {
        const fresh = await eventsApi.getById(event.id)
        if (cancelled) return
        if (fresh?.detection_video_url) {
          setReadyUrl(fresh.detection_video_url)
          // once: the rows (Recent list, Events page) pick the link up as well
          queryClient?.invalidateQueries({ queryKey: queryKeys.events.recent() })
          queryClient?.invalidateQueries({ queryKey: ['events', 'paginated'] })
          return
        }
        // #332 — it failed and will never be ready: stop asking and say so
        if (fresh?.video_status === 'failed') {
          setServerFailed(true)
          return
        }
      } catch (err) {
        if (cancelled) return
        // the event is gone (404) or we may not read it (401/403): nothing to wait for, hide the status.
        // Other errors: keep going until the cap.
        const status = (err as { response?: { status?: number } } | null)?.response?.status
        if (status === 404 || status === 401 || status === 403) stop = true
      }
      if (stop) { setStopped(true); return }
      if (calls >= CLIP_POLL_MAX) { setGaveUp(true); return }
      timer = window.setTimeout(ask, delayFor(calls + 1))
    }
    timer = window.setTimeout(ask, delayFor(1))
    return () => { cancelled = true; if (timer !== undefined) window.clearTimeout(timer) }
  }, [event.id, waiting, fresh, stopped, pollRound, queryClient])

  // #331 — Retry after a load failure: ask the server once for a fresh link. The new link replaces the locked
  // one. The <video> is keyed on the link AND a reload counter, so even an identical link remounts it (a media
  // element never retries by itself). No polling here: the clip exists, only its link is bad. One request at
  // a time: clicks while it is pending are ignored.
  const retryBrokenClip = useCallback(async () => {
    statusRef.current?.focus()
    if (retryingRef.current) return
    retryingRef.current = true
    setRetrying(true)
    try {
      const answer = await eventsApi.getById(event.id)
      if (!mountedRef.current) return
      if (answer?.detection_video_url) {
        setReadyUrl(answer.detection_video_url)
        setReloads((n) => n + 1)
        setVideoPlaying(false)   // the reloaded clip stays hidden until its first frame (#078)
        setVideoError(false)
        setGaveUp(false)
      } else {
        setGaveUp(true)
      }
    } catch {
      if (mountedRef.current) setGaveUp(false)   // keep the error and its Retry
    } finally {
      retryingRef.current = false
      if (mountedRef.current) setRetrying(false)
    }
  }, [event.id])

  // ── Pan helpers ──
  // Clamp offset so media never fully leaves the viewport
  const clampOffset = useCallback(
    (x: number, y: number, s: number): { x: number; y: number } => {
      if (!containerRef.current) return { x, y }
      const { width, height } = containerRef.current.getBoundingClientRect()
      // Max pan = half the "extra" size created by scale
      const maxX = (width * (s - 1)) / 2
      const maxY = (height * (s - 1)) / 2
      return { x: clamp(x, -maxX, maxX), y: clamp(y, -maxY, maxY) }
    },
    []
  )

  // ── Desktop: mouse wheel zoom ──
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return // plain scroll = navigate detail panel, not zoom
      e.preventDefault()
      const delta = e.deltaY > 0 ? -0.15 : 0.15
      setScale((prev) => {
        const next = clamp(prev + delta, MIN_SCALE, MAX_SCALE)
        if (next === MIN_SCALE) setOffset({ x: 0, y: 0 })
        return next
      })
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [])

  // ── Desktop: double-click to zoom in/out ──
  const handleDoubleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Don't zoom when clicking native video controls (bottom ~44px)
      if (isVideo) {
        const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect()
        if (e.clientY > rect.bottom - 48) return
      }
      if (isZoomed) {
        setScale(1)
        setOffset({ x: 0, y: 0 })
      } else {
        setScale(2)
        setOffset({ x: 0, y: 0 })
      }
    },
    [isZoomed, isVideo]
  )

  // ── Desktop: click-drag to pan ──
  const dragStart = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null)

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isZoomed) return
      e.preventDefault()
      dragStart.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y }
      setIsDragging(true)
    },
    [isZoomed, offset]
  )

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!dragStart.current) return
      const dx = e.clientX - dragStart.current.x
      const dy = e.clientY - dragStart.current.y
      setOffset(
        clampOffset(dragStart.current.ox + dx, dragStart.current.oy + dy, scale)
      )
    }
    const onMouseUp = () => {
      dragStart.current = null
      setIsDragging(false)
    }
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [scale, clampOffset])

  // ── Mobile: pinch-to-zoom + single-finger pan ──
  const lastTouches = useRef<{ dist: number; mx: number; my: number; ox: number; oy: number } | null>(null)

  const getTouchDist = (t: React.TouchList) =>
    Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY)

  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (e.touches.length === 2) {
        lastTouches.current = {
          dist: getTouchDist(e.touches),
          mx: (e.touches[0].clientX + e.touches[1].clientX) / 2,
          my: (e.touches[0].clientY + e.touches[1].clientY) / 2,
          ox: offset.x,
          oy: offset.y,
        }
      } else if (e.touches.length === 1 && isZoomed) {
        dragStart.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
          ox: offset.x,
          oy: offset.y,
        }
        setIsDragging(true)
      }
    },
    [offset, isZoomed]
  )

  const handleTouchMove = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (e.touches.length === 2 && lastTouches.current) {
        e.preventDefault()
        const newDist = getTouchDist(e.touches)
        const ratio = newDist / lastTouches.current.dist
        setScale((prev) => {
          const next = clamp(prev * ratio, MIN_SCALE, MAX_SCALE)
          if (next === MIN_SCALE) setOffset({ x: 0, y: 0 })
          return next
        })
        lastTouches.current.dist = newDist
      } else if (e.touches.length === 1 && dragStart.current) {
        e.preventDefault()
        const dx = e.touches[0].clientX - dragStart.current.x
        const dy = e.touches[0].clientY - dragStart.current.y
        setOffset(
          clampOffset(dragStart.current.ox + dx, dragStart.current.oy + dy, scale)
        )
      }
    },
    [scale, clampOffset]
  )

  const handleTouchEnd = useCallback(() => {
    lastTouches.current = null
    dragStart.current = null
    setIsDragging(false)
  }, [])

  // ── Cursor logic — use state so React re-renders on drag start/end ──
  const [isDragging, setIsDragging] = useState(false)
  const cursor = isZoomed ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-default'

  const transformStyle = {
    transform: `scale(${scale}) translate(${offset.x / scale}px, ${offset.y / scale}px)`,
    transformOrigin: 'center center',
    transition: dragStart.current ? 'none' : 'transform 0.15s ease-out',
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative w-full h-full bg-black flex items-center justify-center min-h-0 overflow-hidden select-none',
        cursor,
        // Prevent browser pan/zoom when we handle it ourselves
        isZoomed ? '[touch-action:none]' : '[touch-action:auto]'
      )}
      onDoubleClick={handleDoubleClick}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Media */}
      <div style={transformStyle} className="w-full h-full flex items-center justify-center">
        {isVideo && videoUrl ? (
          // Two-phase reveal — see comment in ModalMedia.
          // Phase 1: thumbnail only. Phase 2 (after modal settles): <video> mounts and fades in.
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Thumbnail is always rendered behind the video so the swap is a paint-only fade.
                Pinned to w-full h-full with object-contain to match the video's letterbox box. */}
            {thumbnailUrl && (
              <img
                src={thumbnailUrl}
                alt=""
                aria-hidden
                draggable={false}
                className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
              />
            )}
            {modalSettled && (
              <video
                key={`${videoUrl}#${reloads}`}
                ref={videoRefCallback}
                src={videoUrl}
                controls
                autoPlay
                loop
                playsInline
                preload="auto"
                onPlaying={() => setVideoPlaying(true)}
                // #331 — ignore an error from an element that was already replaced (removed from the page)
                onError={(e) => {
                  if (!e.currentTarget.isConnected) return
                  setVideoError(true)
                  setGaveUp(false)
                }}
                // Swap, do NOT cross-fade. The thumbnail is the annotated DETECTION frame;
                // the video is already autoplaying from frame 0 — different content, and
                // moving. Ramping the video's opacity over 200ms therefore superimposed two
                // different scenes for a quarter of a second (measured: 0.01 -> 0.23 -> 0.77
                // -> 1), which reads as a ghosting glitch. The opacity gate still earns its
                // keep — it hides the element until it genuinely has a frame to show — but it
                // must flip in one go. What prevents the media-pipeline nudge is the mount
                // delay (modalSettled), not the fade.
                className={cn(
                  'relative w-full h-full object-contain',
                  videoPlaying ? 'opacity-100' : 'opacity-0'
                )}
                // Prevent drag on the video element itself from conflicting
                onMouseDown={(e) => isZoomed && e.preventDefault()}
              />
            )}
          </div>
        ) : imageUrl && imageStatus !== 'error' ? (
          // Pinned to the full box, exactly as the video branch pins its thumbnail. Sized by
          // its own intrinsic dimensions (max-h-full max-w-full) the <img> has no box until it
          // decodes, so it grew from nothing into place inside this centering flex container —
          // while the modal was still running its zoom-in open animation. object-contain
          // letterboxes it identically either way, so the reserved box costs nothing.
          <div className="relative w-full h-full flex items-center justify-center">
            {showSpinner && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-white/40" />
              </div>
            )}
            {/* No opacity gate and no transition. An <img> with no data yet simply paints
                nothing, so the spinner behind it shows through, and the frame paints over
                the spinner the instant it decodes — which for a cached thumbnail is the
                first frame. Fading it in instead meant painting at opacity-0 and then
                ramping to 1 over 300ms, directly across the modal's own 350ms zoom: two
                animations over the same pixels, which is the "glitch" on open.
                Do NOT reintroduce a fade here on the theory that a cached image will skip
                it — `img.complete` is false in the same tick the element is created, even
                from cache, so the ramp always runs. */}
            <img
              ref={imgRef}
              key={imageUrl}
              src={imageUrl}
              alt={event.event_type}
              draggable={false}
              className="absolute inset-0 w-full h-full object-contain"
              onLoad={(e) => {
                setImageStatus('loaded')
                const el = e.currentTarget
                if (fullSizeOnScreen && el.naturalWidth > 0 && el.naturalHeight > 0) {
                  onShownResolution?.(`${el.naturalWidth}x${el.naturalHeight}`)
                }
              }}
              onError={() => setImageStatus('error')}
            />
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 text-white/30">
            <ImageOff className="h-12 w-12" />
            <span className="text-sm">{t('detail.noMedia')}</span>
          </div>
        )}
      </div>

      {/* 409 — superusers: the pose skeleton over the clip, and its "Show pose" switch */}
      {isVideo && videoUrl && modalSettled && (
        <PoseLayer event={event} videoRef={videoElRef} videoKey={`${videoUrl}#${reloads}`} transformKey={`${scale}|${offset.x}|${offset.y}`} />
      )}

      {/* #398 — the full-size photo is on screen: say so, and let the viewer flip to the thumbnail and back */}
      {fullSizeUrl && !isVideo && (
        <div
          className="absolute bottom-3 left-3 flex items-center gap-2 rounded-full bg-black/60 text-white text-xs px-3 py-1.5 select-none"
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
        >
          <span>{showThumb ? t('detail.thumbnail') : t('detail.fullSize')}</span>
          <button type="button" className="underline px-2 py-1" onClick={() => setShowThumb((v) => !v)}>
            {showThumb ? t('detail.fullSize') : t('detail.thumbnail')}
          </button>
        </div>
      )}

      {/* Status pill. #329: a new video's playable copy is still being prepared (offer Retry once we gave up).
          #331: the clip has a link but failed to load (message + Retry that fetches a fresh link).
          #332: the playable copy failed and will never come ("Video unavailable", no Retry, before the others). */}
      {showStatus && (
        <div
          role="status"
          ref={statusRef}
          tabIndex={-1}
          className="absolute bottom-3 left-1/2 -translate-x-1/2 outline-none flex items-center gap-2 rounded-full bg-black/60 backdrop-blur-sm text-white text-xs px-3 py-1.5 select-none"
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
        >
          {clipFailed ? (
            <span>{t('detail.videoUnavailable')}</span>
          ) : gaveUp || videoError ? (
            <>
              <span>{gaveUp ? t('detail.videoNotReady') : t('detail.videoLoadFailed')}</span>
              <button
                type="button"
                disabled={retrying}
                className="underline underline-offset-2 hover:text-white/80 disabled:opacity-50 disabled:no-underline"
                onClick={() => {
                  if (videoError) { void retryBrokenClip(); return }
                  setPollRound((n) => n + 1); statusRef.current?.focus()
                }}
              >
                {t('detail.retry')}
              </button>
            </>
          ) : (
            <>
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
              <span>{t('detail.preparingVideo')}</span>
            </>
          )}
        </div>
      )}

      {/* Zoom level indicator — shown when zoomed */}
      {isZoomed && (
        <div className="absolute top-3 left-3 rounded-full bg-black/60 backdrop-blur-sm text-white text-xs px-2.5 py-1 font-medium pointer-events-none select-none">
          {scale.toFixed(1)}×
        </div>
      )}

      {/* Zoom hint — shown at 1× on desktop to hint double-click */}
      {!isZoomed && (
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 rounded-full bg-black/40 backdrop-blur-sm text-white/50 text-[10px] px-2.5 py-1 pointer-events-none select-none hidden lg:block">
          {t('detail.zoomHint')}
        </div>
      )}
    </div>
  )
}

// ─── Detail panel ─────────────────────────────────────────────────────────────

function DetailPanel({
  event,
  moreOpen,
  onToggleMore,
  fullSize,
  shownResolution,
}: {
  event: Event
  moreOpen: boolean
  onToggleMore: () => void
  fullSize: FullSizeState
  shownResolution: string | null   // #398 — the full-size photo's size while it is on screen
}) {
  const { t } = useTranslation('events')
  const { t: tc } = useTranslation('common')  // #392
  const moreId = useId()
  const title = useEventTitle()

  // #294 — a critical event: the critical title ("Graffiti spray sound · 64 %") and its reason chip
  const eventTypeDisplay = title(event, event.event_type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()))

  const confidenceColor =
    event.confidence_score >= 0.8
      ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
      : event.confidence_score >= 0.5
      ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
      : 'bg-rose-500/10 text-rose-600 dark:text-rose-400'

  // Per-object confidence map from metadata — max confidence per class across all frames
  // (the best detection of each class), not the arbitrary last-frame value. (#095 follow-up)
  const detectionMap: Record<string, number> = {}
  const metaDetections = (event.metadata?.detections as VisualDetection[]) ?? []
  metaDetections.forEach((d) => { detectionMap[d.class_name] = Math.max(detectionMap[d.class_name] ?? 0, d.confidence) })

  const objectClasses = event.detected_objects ?? []
  const audioDetections = event.audio_detections ?? []

  // #094 — use the EVENT's frozen location snapshot (where the photo was taken),
  // NOT the camera's current position. Pre-#094 events have no snapshot (null) and
  // are shown as "location not recorded" rather than pinned to the camera's latest spot.
  const lat = event.latitude != null ? Number(event.latitude) : null
  const lng = event.longitude != null ? Number(event.longitude) : null
  const hasCoords =
    lat != null && lng != null && !Number.isNaN(lat) && !Number.isNaN(lng) && (lat !== 0 || lng !== 0)
  const locationAddress = event.location_address

  const failedReason = fullSize.request?.status === 'failed' ? fullSize.request.reason : null
  const resolution = shownResolution ?? event.media_details?.resolution   // #398
  const { original, annotated } = getEventDownloads(event, fullSize.url ?? undefined)

  const favMutation = useToggleFavorite(event.id) // #122

  return (
    // #244: side by side this panel is its own scroller. Stacked (phones) it takes its natural
    // height and the whole popup scrolls instead — see EventDetailModal.
    <div className="flex flex-col wide:h-full wide:overflow-y-auto">
      {/* Header */}
      <div className="p-4 border-b border-border/60 flex-shrink-0">
        {/* Top row: title + confidence + close */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold leading-tight">{eventTypeDisplay}</h2>
            {isCriticalEvent(event) && (
              <div className="mt-1 flex flex-wrap gap-1.5"><CriticalReasonChips event={event} /></div>
            )}
            {/* #392 — who acknowledged it and when (the Events, Dashboard and Cameras pages all use this popup) */}
            {isCriticalEvent(event) && event.acknowledged_at && (
              <p className="mt-1 text-[13px] font-semibold leading-snug text-emerald-700 dark:text-emerald-400">
                {event.acknowledged_by
                  ? tc('critical.acknowledgedBy', { name: event.acknowledged_by, time: formatCardDateTime(event.acknowledged_at) })
                  : tc('critical.acknowledgedNoName', { time: formatCardDateTime(event.acknowledged_at) })}
              </p>
            )}
            {/* #406 — the orange reasons, who saw it and when, or Seen */}
            {isSuspiciousEvent(event) && <SuspiciousDetails key={event.id} event={event} />}
            <div className="flex items-center gap-1.5 mt-1 text-sm text-muted-foreground">
              <Camera className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="truncate">{event.camera_name}</span>
            </div>
          </div>
          {/* wide:pr-9 keeps clear of the close button, which is pinned to the popup's corner. */}
          <div className="flex items-center gap-2 flex-shrink-0 wide:pr-9">
            <span className={cn('inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold', confidenceColor)}>
              {Math.round(event.confidence_score * 100)}%
            </span>
            {/* #122 — favourite toggle */}
            <button
              type="button"
              onClick={() => favMutation.mutate(!event.is_favorite)}
              aria-label={event.is_favorite ? t('card.unfavorite') : t('card.favorite')}
              title={event.is_favorite ? t('card.unfavorite') : t('card.favorite')}
              className="flex h-11 w-11 wide:h-7 wide:w-7 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            >
              <Star className={cn('h-4 w-4', event.is_favorite && 'fill-amber-400 text-amber-400')} />
            </button>
          </div>
        </div>

        {/* Timestamps */}
        <div className="mt-3 space-y-1">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Calendar className="h-3.5 w-3.5 flex-shrink-0" />
            <span>{formatDateTime(event.occurred_at)}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock className="h-3.5 w-3.5 flex-shrink-0" />
            <span>{t('detail.created', { date: formatDateTime(event.created_at) })}</span>
          </div>
          {/* #094 — capture-time location snapshot (address → coords → not recorded) */}
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
            {hasCoords ? (
              <span className="truncate">
                {locationAddress ?? `${lat!.toFixed(5)}, ${lng!.toFixed(5)}`}
              </span>
            ) : (
              <span className="italic">{t('detail.locationNotRecorded')}</span>
            )}
          </div>
        </div>
      </div>

      {/* 409 — superusers: the clue's answer and the review marks */}
      <PoseReviewPanel event={event} />

      {/* #110 — AI activity caption (featured callout). Renders only when present. */}
      {event.activity_caption && (
        <div className="p-5 pb-4 border-b border-border/60 flex-shrink-0">
          <div
            role="note"
            aria-label={t('detail.activity')}
            className="flex gap-2 rounded-lg border border-primary/20 bg-primary/5 p-3"
          >
            <Sparkles className="h-4 w-4 flex-shrink-0 text-primary mt-0.5" aria-hidden />
            <p className="text-sm text-foreground leading-relaxed">{event.activity_caption}</p>
          </div>
        </div>
      )}

      {/* Detection chips */}
      {(objectClasses.length > 0 || audioDetections.length > 0) && (
        <div className="p-5 pb-4 border-b border-border/60 space-y-3 flex-shrink-0">
          {objectClasses.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground/60 mb-2">
                {t('detail.detectedObjects')}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {objectClasses.map((cls) => (
                  <ModalChip key={cls} cls={cls} confidence={detectionMap[cls]} type="object" />
                ))}
              </div>
            </div>
          )}
          {audioDetections.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground/60 mb-2">
                {t('detail.audioEvents')}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {audioDetections.map((d) => (
                  <ModalChip key={d.class_name} cls={d.class_name} confidence={d.confidence} type="audio" />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* #244 — on phones the secondary details sit behind a toggle so the map and the downloads
          are a short scroll away; side by side there is room, so no toggle and always shown.
          Shown below the map on phones (order-2). */}
      <div data-popup="more" className="order-2 flex-shrink-0 wide:order-none">
        <button
          type="button"
          aria-expanded={moreOpen}
          aria-controls={moreId}
          onClick={onToggleMore}
          className="flex min-h-[44px] w-full items-center justify-between border-b border-border/60 px-5 py-3 text-sm font-medium text-foreground hover:bg-accent/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring wide:hidden"
        >
          {t('detail.moreDetails')}
          <ChevronDown
            className={cn('h-4 w-4 text-muted-foreground transition-transform motion-reduce:transition-none', moreOpen && 'rotate-180')}
            aria-hidden
          />
        </button>
        <div id={moreId} className={cn(moreOpen ? 'block' : 'hidden', 'wide:block')}>
          {/* Description */}
          {event.description && (
            <div className="p-5 pb-4 border-b border-border/60 flex-shrink-0">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground/60 mb-2">
                {t('detail.description')}
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed">{event.description}</p>
            </div>
          )}

          {/* Metadata grid */}
          <div className="p-5 pb-4 border-b border-border/60 flex-shrink-0">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground/60 mb-3">
              {t('detail.eventInfo')}
            </p>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5">
              <div>
                <dt className="text-[10px] text-muted-foreground/60 uppercase tracking-wide">{t('detail.eventId')}</dt>
                <dd className="text-xs font-medium mt-0.5">#{event.id}</dd>
              </div>
              <div>
                <dt className="text-[10px] text-muted-foreground/60 uppercase tracking-wide">{t('detail.mediaType')}</dt>
                <dd className="text-xs font-medium mt-0.5 capitalize">
                  {event.media_details?.media_type ?? t('types.image')}
                </dd>
              </div>
              {resolution && (
                <div>
                  <dt className="text-[10px] text-muted-foreground/60 uppercase tracking-wide">{t('detail.resolution')}</dt>
                  <dd className="text-xs font-medium mt-0.5">{resolution}</dd>
                </div>
              )}
              {event.media_details?.duration != null && (
                <div>
                  <dt className="text-[10px] text-muted-foreground/60 uppercase tracking-wide">{t('detail.duration')}</dt>
                  <dd className="text-xs font-medium mt-0.5">{event.media_details.duration}s</dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      </div>

      {/* #243 — where it happened: satellite map by default, Street View in the same box */}
      {hasCoords && <EventLocationMap lat={lat!} lng={lng!} className="order-1 wide:order-none" />}

      {/* #247 — the full location block under the map (the popup pin is a plain pin: no hover card) */}
      {hasCoords && (
        <div className="order-1 wide:order-none flex-shrink-0 border-b border-border/60 px-5 py-3">
          <LocationDetails lat={lat!} lng={lng!} address={locationAddress} />
        </div>
      )}

      {/* Actions */}
      <div data-popup="actions" className="order-3 p-5 mt-auto flex-shrink-0 space-y-2 wide:order-none">
        {annotated && (
          <Button
            variant="outline"
            className="w-full gap-2"
            onClick={() => downloadFile(annotated.url, annotated.filename)}
          >
            <Download className="h-4 w-4" />
            {t('detail.downloadAnnotated')}
          </Button>
        )}
        {/* #398 — ask the camera for the full-size photo of this thumbnail */}
        {event.media_details?.can_request_full_size && !fullSize.url && (
          <>
            {failedReason && (
              <p className="text-xs text-destructive">{t('detail.fullSizeFailed', { reason: failedReason })}</p>
            )}
            {fullSize.error && (
              <p className="text-xs text-destructive">
                {fullSize.error === 'network' ? t('detail.fullSizeError') : fullSize.error}
              </p>
            )}
            {(!fullSize.request || fullSize.request.status === 'failed') && (
              <Button variant="outline" className="w-full gap-2" disabled={fullSize.asking} onClick={fullSize.ask}>
                {t('detail.getFullSize')}
              </Button>
            )}
            {fullSize.request?.status === 'waiting' && !fullSize.stale && (
              <Button variant="outline" className="w-full gap-2 h-auto whitespace-normal py-2 text-left" disabled>
                <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                {t('detail.fullSizeWaiting')}
              </Button>
            )}
            {fullSize.request?.status === 'waiting' && fullSize.stale && (
              <p role="status" className="text-xs text-muted-foreground">{t('detail.fullSizeStillWaiting')}</p>
            )}
          </>
        )}
        {original && (
          <Button
            variant="ghost"
            className="w-full gap-2 text-muted-foreground"
            onClick={() => downloadFile(original.url, original.filename)}
          >
            <Download className="h-4 w-4" />
            {t('detail.downloadOriginal')}
          </Button>
        )}
      </div>
    </div>
  )
}

// ─── Main modal ───────────────────────────────────────────────────────────────

interface EventDetailModalProps {
  events: Event[]
  selectedIndex: number | null
  /** #292 — the event the parent asks to show (the Events page's `?event=` id). When it changes,
   *  the popup switches to that event as soon as it is in `events`. Needed where the parent can
   *  open a different event at an unchanged index (the fetch-by-id fallback opens at 0). */
  targetId?: number | null
  onClose: () => void
  onNavigate: (index: number) => void
}

export function EventDetailModal({
  events,
  selectedIndex,
  targetId = null,
  onClose,
  onNavigate,
}: EventDetailModalProps) {
  const { t } = useTranslation('events')

  // #292 — the popup follows the EVENT, not the position. Rows now arrive live, so a new event
  // at the top shifts the list under an open popup, and on "Unread only" the event just opened
  // leaves the list on the next refetch; holding only the index would silently switch to another
  // event. `tracked` is the event the user opened or arrowed to. When the parent moves the index
  // (open, arrows) it is re-read from the list; when only the list changes it is found again by
  // id, and if it has left the list its last copy stays on screen.
  const [tracked, setTracked] = useState<Event | null>(
    selectedIndex !== null ? events[selectedIndex] ?? null : null)
  const [trackedIndex, setTrackedIndex] = useState(selectedIndex)
  const [honoredTarget, setHonoredTarget] = useState<number | null>(null)
  if (selectedIndex !== trackedIndex) {
    setTrackedIndex(selectedIndex)
    setTracked(selectedIndex !== null ? events[selectedIndex] ?? null : null)
    if (selectedIndex === null) setHonoredTarget(null)
  }
  if (selectedIndex !== null && targetId !== null && targetId !== honoredTarget) {
    const target = events.find((e) => e.id === targetId)
    if (target) {
      setHonoredTarget(targetId)
      if (tracked?.id !== targetId) setTracked(target)
    }
  }
  const liveIndex = tracked ? events.findIndex((e) => e.id === tracked.id) : -1
  const event = selectedIndex === null || !tracked ? null : liveIndex >= 0 ? events[liveIndex] : tracked
  // Where the arrows count from: the event's place now, or — once it has left the list — the
  // place it had, where the next event now sits.
  const base = liveIndex >= 0 ? liveIndex : (selectedIndex ?? 0)
  const isOpen = event !== null
  const fullSize = useFullSizePhoto(event, useOptionalQueryClient())  // #398
  const hasPrev = isOpen && base > 0
  const hasNext = isOpen && (liveIndex >= 0 ? base < events.length - 1 : base < events.length)
  const prevIndex = base - 1
  const nextIndex = liveIndex >= 0 ? base + 1 : base

  // Keep the parent's index on the event, so its own handlers (e.g. a fresh video fetch on the
  // Events page) and the "n / total" counter stay right.
  useEffect(() => {
    if (liveIndex >= 0 && selectedIndex !== null && liveIndex !== selectedIndex) onNavigate(liveIndex)
  }, [liveIndex, selectedIndex, onNavigate])

  const goTo = useCallback((index: number) => {
    const next = events[index]
    if (!next) return
    setTracked(next)
    setTrackedIndex(index)
    onNavigate(index)
  }, [events, onNavigate])

  // #292 — the event on screen counts as read for this person, once per event.
  const markRead = useMarkEventRead()
  const markedRef = useRef<Set<number>>(new Set())
  const shownId = event?.id
  const shownUnread = event?.is_unread === true
  useEffect(() => {
    if (shownId === undefined || !shownUnread || markedRef.current.has(shownId)) return
    markedRef.current.add(shownId)
    markRead.mutate(shownId)
  }, [shownId, shownUnread, markRead])
  // A closed popup forgets, so a call that failed is tried again the next time the event opens.
  useEffect(() => { if (!isOpen) markedRef.current.clear() }, [isOpen])

  // #244: "More details" lives here, not in DetailPanel (which remounts per event), so opening
  // it once keeps it open while paging through events.
  const [moreOpen, setMoreOpen] = useState(false)
  // #398 — the Resolution line follows the photo on screen (ModalMedia reports the full-size photo's size);
  // a new event starts from its media row again (reset below, with the scroll, before paint)
  const [shownResolution, setShownResolution] = useState<string | null>(null)

  // #244: stacked, the whole popup is one scroller and it outlives the event. Start each event
  // from the top, as the side-by-side panel always has (it remounts per event).
  const scrollRef = useRef<HTMLDivElement>(null)
  const eventId = event?.id
  useLayoutEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0
    setShownResolution(null)   // #398
  }, [eventId])

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return
    const handleKey = (e: KeyboardEvent) => {
      // #243: the map and Street View pan and walk with these same keys. While the location
      // map has focus they belong to it, or walking down a street would flip through events.
      if (e.target instanceof Element && e.target.closest('[data-event-location-map]')) return
      if (e.key === 'ArrowLeft' && hasPrev) goTo(prevIndex)
      if (e.key === 'ArrowRight' && hasNext) goTo(nextIndex)
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [isOpen, hasPrev, hasNext, prevIndex, nextIndex, goTo])

  if (!event) return null

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose() }}>
      <Dialog.Portal>
        {/* Backdrop — slightly slower than modal so it lingers a touch */}
        <Dialog.Overlay
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:duration-250 data-[state=open]:duration-300"
          style={{ animationTimingFunction: 'cubic-bezier(0.32, 0.72, 0, 1)' }}
        />

        {/* Modal container */}
        <Dialog.Content
          className={cn(
            'fixed z-50 inset-0 m-auto',
            // Size: near fullscreen on mobile, constrained on desktop
            // dvh, not vh: on mobile browsers 100vh EXCLUDES the address bar, so the modal
            // was taller than the visible viewport and the detail panel and the video's own
            // controls sat underneath the browser chrome. dvh tracks the bar as it hides.
            //
            // The width cap is set by the source media, not by taste. Cameras transmit
            // 896x504 (measured on the fleet), and thumbnail_url IS that frame full-size —
            // there is no larger image anywhere. Past roughly 1.2x native the image is only
            // being interpolated, so growing the modal further buys blur, not detail. At
            // 1400px the media column lands near 1000px: a touch over native, still crisp,
            // and a third wider than the old max-w-6xl (1152px) allowed.
            //
            // #244: on a phone the popup IS the screen — no margin, border or rounding to spend
            // pixels on. From 640px up it is the card described above.
            'w-full h-[100dvh] max-w-[1400px] rounded-none border-0',
            'sm:w-[calc(100vw-2rem)] sm:h-[calc(100dvh-2rem)] sm:max-h-[92dvh] sm:rounded-xl sm:border',
            'bg-background border-border shadow-2xl overflow-hidden',
            'flex flex-col',
            // Animations — open: scale up + fade + subtle rise; close: reverse
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=open]:zoom-in-90 data-[state=closed]:zoom-out-95',
            'data-[state=open]:slide-in-from-bottom-3 data-[state=closed]:slide-out-to-bottom-1',
            'data-[state=open]:duration-350 data-[state=closed]:duration-200',
          )}
          style={{ animationTimingFunction: 'cubic-bezier(0.32, 0.72, 0, 1)' }}
          aria-describedby={undefined}
        >
          <Dialog.Title className="sr-only">
            {t('detail.title', { type: event.event_type })}
          </Dialog.Title>

          {/* Content — side by side wherever there is room for it (see the `wide` screen in
              tailwind.config.js), stacked otherwise. Keyed on aspect ratio, not width alone,
              so a phone in landscape gets the columns instead of stacking into 390px of height.

              #244 — stacked, THIS is the one scroller: the media is pinned at the top and
              everything else scrolls under it. It used to be `overflow-hidden` with the media
              as `flex-1 min-h-0` and the details at their natural height, so on a phone the
              details took the whole popup, the media got the height that was left (none), and
              the map and download buttons were clipped with no way to reach them. */}
          <div
            ref={scrollRef}
            data-popup="scroll"
            className="flex flex-col flex-1 min-h-0 overflow-y-auto overscroll-contain wide:flex-row wide:overflow-hidden"
          >
            {/* Media panel — stacked: pinned, in the 16:9 the cameras send, and never more than
                45% of the screen so the details keep more than half. Side by side: the left
                column, as before. */}
            <div
              data-popup="media"
              className="sticky top-0 z-20 w-full shrink-0 aspect-video max-h-[45dvh] bg-black overflow-hidden wide:relative wide:z-auto wide:aspect-auto wide:max-h-none wide:flex-1 wide:min-h-0 wide:rounded-tl-xl wide:rounded-bl-xl"
            >
              {/* Fills the box by position, not by a percentage height: the box's height comes
                  from its aspect ratio when stacked and from the flex row when side by side. */}
              <div className="absolute inset-0">
                <ModalMedia key={event.id} event={event} fullSizeUrl={fullSize.url} onShownResolution={setShownResolution} />
              </div>

              {/* Nav arrows overlaid on media */}
              {hasPrev && (
                <button
                  onClick={() => goTo(prevIndex)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-black/50 backdrop-blur-sm text-white hover:bg-black/70 transition-colors"
                  aria-label={t('navigation.previous')}
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
              )}
              {hasNext && (
                <button
                  onClick={() => goTo(nextIndex)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-black/50 backdrop-blur-sm text-white hover:bg-black/70 transition-colors"
                  aria-label={t('navigation.next')}
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              )}

              {/* Position counter + keyboard hint. Never takes a tap meant for the video's own
                  controls underneath it; stacked, it sits at the top, clear of them altogether. */}
              <div
                data-popup="counter"
                className="pointer-events-none absolute left-1/2 top-3 -translate-x-1/2 flex flex-col items-center gap-1 wide:top-auto wide:bottom-3"
              >
                <div className="rounded-full bg-black/50 backdrop-blur-sm text-white text-xs px-3 py-1 font-medium">
                  {selectedIndex! + 1} / {events.length}
                </div>
                {events.length > 1 && (
                  <span className="text-[10px] text-white/50 hidden sm:block">
                    ← → {t('navigation.keyboardHint', { defaultValue: 'to navigate' })}
                  </span>
                )}
              </div>
            </div>

            {/* Detail panel — `isolate` keeps its own z-indexes (the map's switch) underneath the
                pinned media as they scroll past. */}
            <div
              data-popup="details"
              className="isolate w-full flex-shrink-0 border-t border-border/60 wide:w-80 xl:w-96 wide:border-t-0 wide:border-l wide:overflow-hidden"
            >
              <DetailPanel
                key={event.id}
                event={event}
                fullSize={fullSize}
                shownResolution={shownResolution}
                moreOpen={moreOpen}
                onToggleMore={() => setMoreOpen((open) => !open)}
              />
            </div>
          </div>

          {/* #244 — one close button, pinned to the popup's corner so it cannot scroll away:
              over the media when stacked (finger-sized, white on a dark disc), and where it has
              always been, in the panel's header corner, when side by side. After the content in
              the DOM so that opening the popup focuses what it did before.

              No onClick here. Dialog.Close already routes this through
              Dialog.Root's onOpenChange, and Radix COMPOSES a child's onClick
              with its own rather than replacing it — so an onClick={onClose}
              would fire the close twice. That is harmless for an idempotent
              handler but not for the feed's, which pops history (navigate(-1))
              and would pop twice, landing the user a page further back than
              they asked for. onOpenChange is the single close channel. */}
          <Dialog.Close asChild>
            <button
              className={cn(
                'absolute right-2 top-2 z-30 flex h-11 w-11 items-center justify-center rounded-full transition-colors',
                'bg-black/50 text-white backdrop-blur-sm hover:bg-black/70',
                // Side by side it floats over the panel, which scrolls underneath it: a near-opaque
                // disc of the panel's own colour is invisible at rest and keeps it legible then.
                'wide:right-4 wide:top-4 wide:h-7 wide:w-7 wide:bg-background/80 wide:text-muted-foreground wide:hover:bg-accent wide:hover:text-foreground',
              )}
              aria-label={t('detail.close')}
            >
              <X className="h-5 w-5 wide:h-4 wide:w-4" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
